import type { ReactNode, VoidFunctionComponent } from 'react';
import type Stripe from 'stripe';

import { window } from '../../../../globals';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button/button-constants';
import NavigationButton from '../../../generic/Button/NavigationButton';
import Flex from '../../../generic/Flex';
import {
  getCurrentPlanType,
  getDisplayPlanPrice,
  getDisplayPlanType,
  getDynamicSubscriptionPlanName,
  getSubscriptionPlanName,
  getUsage,
} from '../utils';
import type { FossaSubscriptionPlan } from '../../../../types';

import styles from '../style.module.less';

type PlanInfoProps = {
  data: FossaSubscriptionPlan
  freePrice: Stripe.Price,
};
const PlanInfo: VoidFunctionComponent<PlanInfoProps> = ({
  data,
  freePrice,
}) => {
  const currentPlanInfo = [
    {
      title: 'Customer',
      value: window.user?.organization?.title || '',
    },
    {
      title: 'Current Plan',
      value: <DisplayPlanName data={data} freePrice={freePrice} />,
    },
    {
      title: 'Plan Price',
      value: getDisplayPlanPrice(data),
    },
    {
      title: 'Plan Type',
      value: getDisplayPlanType(data),
    },
    {
      title: 'Usage',
      value: getUsage(data),
    },
  ];

  return (
    <Flex justify="space-between">
      {currentPlanInfo.map((info) => {
        return (
          <DisplayInfo key={info.title} title={info.title}>
            {info.value}
          </DisplayInfo>
        );
      })}
    </Flex>
  );
};

type DisplayInfoProps = {
  title: string,
  children: ReactNode | string,
};
const DisplayInfo: VoidFunctionComponent<DisplayInfoProps> = ({
  title,
  children,
}) => {
  return (
    <Flex direction="column" align="flex-start">
      <div className={styles.currentPlanInfoLabel}>{title}</div>
      <div className={styles.currentPlanInfoValue}>{children}</div>
    </Flex>
  );
};

const DisplayPlanName: VoidFunctionComponent<PlanInfoProps> = ({
  data,
  freePrice,
}) => {
  const org = window?.user?.organization;
  if (!org) {
    return null;
  }

  const planType = getCurrentPlanType(org);
  const hasCustomBilling = window?.user?.organization?.custom_billing;
  const isOnPrem = window.on_prem;
  const shouldShowLinkToPlans = !hasCustomBilling && !isOnPrem;

  const name = planType === 'FREE'
    ? getDynamicSubscriptionPlanName(freePrice?.product as Stripe.Product)
    : getSubscriptionPlanName(data);

  return (
    <Flex gap={8}>
      {name}
      {shouldShowLinkToPlans &&
        <NavigationButton
          to="/account/settings/billing?tab=Plans"
          type={BUTTON_TYPE.OUTLINE}
          size={BUTTON_SIZE.SMALL}
        >
          Change Plan
        </NavigationButton>}
    </Flex>
  );
};

export default PlanInfo;
